/**
 * PublicStoreSection — Vitrine de produtos na landing pública do tenant.
 *
 * Renderiza somente se o tenant tiver produtos com `is_public=true` e estoque > 0.
 * Cada produto tem um botão "Reservar" que abre um modal simples com:
 *  - nome, telefone, email (opcional), quantidade, observações
 *
 * Ao confirmar, chama POST /api/public/booking/{slug}/product-reservation
 * que baixa o estoque via FIFO e registra o pagamento como "fiado".
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ShoppingBag, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtBRL = (v) =>
  (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function PublicStoreSection({ slug }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/booking/${slug}/products`);
        if (!cancelled) setProducts(data.items || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return null;
  if (products.length === 0) return null;

  return (
    <section className="space-y-4" data-testid="public-store-section">
      <div className="flex items-center gap-2">
        <ShoppingBag className="w-4 h-4 text-tenant" />
        <h2 className="font-display text-lg font-semibold">Nossa loja</h2>
        <div className="text-xs text-muted-foreground">
          {products.length} produto{products.length > 1 ? "s" : ""} disponível{products.length > 1 ? "eis" : ""}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="rounded-xl border border-border bg-card overflow-hidden flex flex-col"
            data-testid={`public-product-${p.id}`}
          >
            {p.image_url && (
              <img
                src={p.image_url}
                alt={p.name}
                className="w-full h-40 object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <div className="p-3 space-y-1 flex-1 flex flex-col">
              <div className="font-semibold text-sm">{p.name}</div>
              {p.brand && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.brand}</div>}
              {p.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>
              )}
              <div className="flex items-end justify-between mt-2 gap-2">
                <div>
                  <div className="font-display font-bold text-lg text-tenant">
                    {fmtBRL(p.price_sale)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {p.available_stock} {p.unit} em estoque
                  </div>
                </div>
                <button
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-white shrink-0 hover:opacity-90 transition-opacity"
                  style={{ background: "hsl(var(--tenant-primary))" }}
                  onClick={() => setTarget(p)}
                  data-testid={`public-product-reserve-${p.id}`}
                >
                  Reservar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {target && (
        <ReserveDialog
          slug={slug}
          product={target}
          onClose={() => setTarget(null)}
          onDone={async () => {
            setTarget(null);
            // refresh stock counts
            try {
              const { data } = await axios.get(`${API}/public/booking/${slug}/products`);
              setProducts(data.items || []);
            } catch { /* ignore */ }
          }}
        />
      )}
    </section>
  );
}


function ReserveDialog({ slug, product, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", quantity: 1, notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/public/booking/${slug}/product-reservation`,
        {
          name: form.name,
          phone: form.phone,
          email: form.email || undefined,
          items: [{ product_id: product.id, quantity: Number(form.quantity) }],
          notes: form.notes || undefined,
        },
      );
      toast.success(`Reserva confirmada! Total: ${fmtBRL(data.total)}. Aguarde retorno da loja.`);
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível reservar. Tente novamente.";
      toast.error(typeof msg === "string" ? msg : "Erro ao reservar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose} data-testid="reserve-dialog">
      <div
        className="w-full max-w-md rounded-2xl bg-card border border-border p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Reservar</div>
            <div className="font-display font-bold text-lg truncate">{product.name}</div>
            <div className="text-sm text-tenant font-semibold">{fmtBRL(product.price_sale)}</div>
          </div>
          <button
            onClick={onClose} aria-label="Fechar"
            className="w-8 h-8 rounded-md hover:bg-muted grid place-items-center shrink-0"
            data-testid="reserve-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nome completo *</label>
            <input required minLength={2} maxLength={120} type="text"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="reserve-name"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">WhatsApp *</label>
              <input required type="tel" placeholder="(11) 99999-0000"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="reserve-phone"
                className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Quantidade</label>
              <input required type="number" min="1" max={product.available_stock} step="1"
                value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="reserve-qty"
                className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">E-mail (opcional)</label>
            <input type="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="reserve-email"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Observações</label>
            <textarea rows={2} maxLength={500}
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="reserve-notes"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
          </div>

          <div className="pt-2 flex items-center justify-between gap-2">
            <div className="text-sm">
              <span className="text-muted-foreground">Total: </span>
              <span className="font-bold text-tenant text-lg">
                {fmtBRL((product.price_sale || 0) * (Number(form.quantity) || 0))}
              </span>
            </div>
            <button type="submit" disabled={busy}
              className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-60 hover:opacity-90"
              style={{ background: "hsl(var(--tenant-primary))" }}
              data-testid="reserve-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Confirmar reserva"}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Você reserva agora e paga na retirada. Nossa equipe entra em contato pelo WhatsApp informado.
          </p>
        </form>
      </div>
    </div>
  );
}
